/*
 * <delivery-window>: dates of delivery for an order placed now, in the cart drawer and on the cart page (GMC-081,
 * CHAT-055). The same script as the Delivery estimate block of the product page (blocks/delivery-estimate.liquid),
 * whose block script only loads on a page that renders that block: the drawer, rendered on every page and filled after
 * an add to cart, and the cart page load this file instead. Whichever loads first defines the element; the other one
 * leaves it as is. Keep both scripts the same.
 */
(() => {
  if (customElements.get('delivery-window')) return;

  const DAY_MS = 86400000;
  // A calendar day is counted as whole days since 1970-01-01: no time zone or daylight-saving change can shift it.
  const dayOf = (year, month, date) => Math.round(Date.UTC(year, month - 1, date) / DAY_MS);
  const weekdayOf = (day) => (((day + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday
  const nthWeekday = (year, month, weekday, nth) => {
    if (nth > 0) {
      const first = dayOf(year, month, 1);
      return first + ((weekday - weekdayOf(first) + 7) % 7) + (nth - 1) * 7;
    }
    const last = dayOf(year, month + 1, 1) - 1;
    return last - ((weekdayOf(last) - weekday + 7) % 7);
  };
  // A holiday on a fixed date is observed the Friday before when it falls on a Saturday, the Monday after on a Sunday.
  const observed = (day) => (weekdayOf(day) === 6 ? day - 1 : weekdayOf(day) === 0 ? day + 1 : day);
  // The 11 US federal holidays of a year (observed dates). New Year's Day of the next year may be observed on Dec 31.
  const federalHolidays = (year) => [
    observed(dayOf(year, 1, 1)),
    nthWeekday(year, 1, 1, 3),
    nthWeekday(year, 2, 1, 3),
    nthWeekday(year, 5, 1, -1),
    observed(dayOf(year, 6, 19)),
    observed(dayOf(year, 7, 4)),
    nthWeekday(year, 9, 1, 1),
    nthWeekday(year, 10, 1, 2),
    observed(dayOf(year, 11, 11)),
    nthWeekday(year, 11, 4, 4),
    observed(dayOf(year, 12, 25)),
  ];

  const clockIn = (now, timeZone) => {
    const parts = {};
    const format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    for (const {type, value} of format.formatToParts(now)) parts[type] = Number(value);
    return {day: dayOf(parts.year, parts.month, parts.day), minutes: (parts.hour % 24) * 60 + parts.minute};
  };

  /**
   * First and last delivery days (whole days since 1970-01-01) for an order placed at `now`.
   * @param {Date} now
   * @param {Object} plan - minDays and maxDays (business days), cutoff (HH:MM or none), timeZone (IANA), holidays (us_federal or none)
   */
  const deliveryDays = (now, plan) => {
    const {minDays, maxDays, cutoff, timeZone, holidays} = plan;
    if (!Number.isInteger(minDays) || !Number.isInteger(maxDays) || minDays < 1 || maxDays < minDays || maxDays > 90) {
      throw new RangeError('Delivery days must be whole numbers, 1 or more, the shortest first.');
    }
    const cutoffMatch = /^(\d{2}):(\d{2})$/.exec(cutoff || '');
    if (cutoff && cutoff !== 'none' && !cutoffMatch) throw new RangeError('The order cutoff must be HH:MM.');
    const clock = clockIn(now, timeZone);
    const holidaysByYear = new Map();
    const closed = (day) => {
      const weekday = weekdayOf(day);
      if (weekday === 0 || weekday === 6) return true;
      if (holidays !== 'us_federal') return false;
      const year = new Date(day * DAY_MS).getUTCFullYear();
      if (!holidaysByYear.has(year)) {
        holidaysByYear.set(year, new Set([...federalHolidays(year), ...federalHolidays(year + 1)]));
      }
      return holidaysByYear.get(year).has(day);
    };
    let start = clock.day;
    const late = cutoffMatch && clock.minutes >= Number(cutoffMatch[1]) * 60 + Number(cutoffMatch[2]);
    if (closed(start) || late) {
      do start += 1;
      while (closed(start));
    }
    const after = (count) => {
      let day = start;
      for (let left = count; left > 0; ) {
        day += 1;
        if (!closed(day)) left -= 1;
      }
      return day;
    };
    return {start, first: after(minDays), last: after(maxDays)};
  };

  const dateLabel = new Intl.DateTimeFormat('en-US', {timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric'});
  const showDay = (day) => dateLabel.format(new Date(day * DAY_MS));
  const deliveryLabel = (now, plan) => {
    const {first, last} = deliveryDays(now, plan);
    return first === last ? showDay(first) : `${showDay(first)} – ${showDay(last)}`;
  };

  class DeliveryWindow extends HTMLElement {
    static deliveryDays = deliveryDays;
    static deliveryLabel = deliveryLabel;

    connectedCallback() {
      this.update();
      // The product section (variant change) or the cart summary (cart update) may be morphed: put the dates back if
      // the range comes back.
      this.observer ??= new MutationObserver(() => this.update());
      this.observer.observe(this, {childList: true, characterData: true, subtree: true});
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    update() {
      try {
        const label = deliveryLabel(new Date(), {
          minDays: Number(this.dataset.minDays),
          maxDays: Number(this.dataset.maxDays),
          cutoff: this.dataset.cutoff,
          timeZone: this.dataset.timeZone,
          holidays: this.dataset.holidays,
        });
        if (this.textContent !== label) this.textContent = label;
      } catch (error) {
        // Keep the range in business days written by the page.
      }
    }
  }

  customElements.define('delivery-window', DeliveryWindow);
})();
